import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtMoney, cycleShort, fmtDate } from "@/lib/fmt";
import { toast } from "sonner";

const ACTION_LABEL = {
  remove_duplicate: "Duplicate — remove",
  mark_twin_paid: "Mark quarter as paid, then remove",
  keep_relabel: "No quarterly match — keep & relabel (please check)",
};

export const HalfYearMergePanel = ({ onMerged }) => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/payments/half-year-merge");
    setPreview(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/half-year-merge");
      const kept = data.rows.filter((r) => r.action === "keep_relabel").length;
      toast.success(`${data.merged - kept} merged into quarters${kept ? `, ${kept} relabelled as a quarter — please check` : ""}`);
      setOpen(false);
      await load();
      onMerged?.();
    } catch (e) {
      toast.error("Merge failed — " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  if (!preview || preview.count === 0) return null;
  return (
    <>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 flex items-center justify-between gap-4 flex-wrap" data-testid="half-year-merge-banner">
        <p>
          <strong>{preview.count} old half-year payment records</strong> are still in the database. They&rsquo;re hidden here and not counted.
          Review and fold them into their matching quarters so everything is quarterly only.
        </p>
        <button className="hh-btn-outline whitespace-nowrap" onClick={() => setOpen(true)} data-testid="half-year-merge-review-btn">Review &amp; merge</button>
      </div>
      {open && <MergeModal rows={preview.rows} busy={busy} onClose={() => setOpen(false)} onApply={apply} />}
    </>
  );
};

function MergeModal({ rows, busy, onClose, onApply }) {
  return (
    <div className="fixed inset-0 bg-hh-ink/40 backdrop-blur-sm flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="half-year-merge-modal">
        <div className="px-6 py-4 border-b border-hh-border">
          <p className="hh-overline">Fold half-year records into quarters</p>
          <p className="text-sm text-hh-ink/70 mt-1">Each old record is matched to the quarterly record with the same author, currency and amount. Removed records are archived, not destroyed. Download a payments backup first if you&rsquo;d like.</p>
        </div>
        <div className="overflow-y-auto px-6 py-3">
          <table className="w-full text-xs">
            <thead className="text-hh-ink/60 text-left">
              <tr><th className="py-1.5">Author</th><th>Old record</th><th className="text-right">Amount</th><th className="pl-4">Quarter record</th><th className="pl-4">What happens</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.legacy_id} className={`border-t border-hh-border/40 ${r.action === "keep_relabel" ? "bg-amber-50" : ""}`} data-testid={`merge-row-${r.legacy_id}`}>
                  <td className="py-1.5">{r.author_name}</td>
                  <td>{cycleShort(r.legacy_cycle)} · {r.legacy_status === "paid" ? `paid ${fmtDate(r.legacy_paid_date)}` : "pending"}</td>
                  <td className="text-right tabular-nums">{fmtMoney(r.amount, r.currency)}</td>
                  <td className="pl-4">{r.quarter ? cycleShort(r.quarter) : "—"}{r.quarter_status ? ` · ${r.quarter_status === "paid" ? `paid ${fmtDate(r.quarter_paid_date)}` : "pending"}` : ""}</td>
                  <td className="pl-4">{ACTION_LABEL[r.action]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-hh-border flex justify-end gap-3">
          <button className="hh-btn-outline" onClick={onClose} data-testid="half-year-merge-cancel">Cancel</button>
          <button className="hh-btn-primary" disabled={busy} onClick={onApply} data-testid="half-year-merge-confirm">{busy ? "Merging…" : `Merge ${rows.length} records`}</button>
        </div>
      </div>
    </div>
  );
}
